import axios, { type AxiosInstance } from 'axios'

/**
 * Insert trailing slash on the path segment, before `?query`, for gateway variants.
 * `/api/v1/foo/bar?x=1` → `/api/v1/foo/bar/?x=1`
 */
export function pathWithTrailingSlashBeforeQuery(pathAndQuery: string): string {
  const q = pathAndQuery.indexOf('?')
  if (q === -1) {
    const p = pathAndQuery
    return p.endsWith('/') ? p : `${p}/`
  }
  const pathPart = pathAndQuery.slice(0, q)
  const query = pathAndQuery.slice(q)
  const fixed = pathPart.endsWith('/') ? pathPart : `${pathPart}/`
  return fixed + query
}

export async function getWithPathFallback(
  client: AxiosInstance,
  primaryPath: string,
  alternatePath: string,
): Promise<unknown> {
  try {
    const response = await client.get(primaryPath)
    return response.data
  } catch (e) {
    if (!axios.isAxiosError(e)) {
      throw e
    }

    const primaryData = e.response?.data as
      | { detail?: unknown; message?: unknown }
      | undefined
    const primaryDetail =
      typeof primaryData?.detail === 'string'
        ? primaryData.detail
        : typeof primaryData?.message === 'string'
          ? primaryData.message
          : null

    const status = e.response?.status
    if (status === 404 || status === 405) {
      try {
        const response = await client.get(alternatePath)
        return response.data
      } catch (alternateError) {
        if (!axios.isAxiosError(alternateError)) {
          throw alternateError
        }

        if (primaryDetail) {
          throw e
        }
        throw alternateError
      }
    }
    throw e
  }
}

export async function postWithPathFallback(
  client: AxiosInstance,
  primaryPath: string,
  alternatePath: string,
  body: unknown,
): Promise<unknown> {
  try {
    const response = await client.post(primaryPath, body)
    return response.data
  } catch (e) {
    if (!axios.isAxiosError(e)) {
      throw e
    }
    const status = e.response?.status
    if (status === 404 || status === 405) {
      const response = await client.post(alternatePath, body)
      return response.data
    }
    throw e
  }
}

export function getAxiosErrorStatus(error: unknown): number | undefined {
  return axios.isAxiosError(error) ? error.response?.status : undefined
}

export function getAxiosErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { detail?: unknown; message?: string }
      | undefined
    if (data?.detail != null) {
      if (typeof data.detail === 'string') {
        return data.detail
      }
      if (Array.isArray(data.detail)) {
        const first = data.detail[0] as { msg?: string } | undefined
        if (first?.msg) {
          return first.msg
        }
      }
    }
    if (typeof data?.message === 'string') {
      return data.message
    }
    if (error.message) {
      return error.message
    }
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'Request failed.'
}
